import './styles/UploadLicense.css';

import { type FC, memo, useRef, useState } from 'react';

import { sleep } from '@ipfabric/ui-components/utils/sleep';
import { prop } from '@ipfabric/utils/fp/prop';
import { Button } from 'components/Button/Button';
import { ButtonAsA } from 'components/Button/ButtonAsA';
import DropzoneWrapper from 'components/DropzoneWrapper';
import {
  type LicenseInfoApiResponse,
  useGlobalStateContextSelectors,
} from 'components/GlobalState/GlobalStateModel';
import { saveLicense } from 'components/GlobalState/commands/saveLicense';
import { useLocale } from 'containers/App/LocaleContext';
import LTEBox from 'containers/LTEBox';
import { EulaModal } from 'pages/Auth/Eula/EulaModal';
import Dropzone, { type DropzoneRef } from 'react-dropzone';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useIpfLocation } from 'utils/history/useIpfLocation';
import { useApi } from 'utils/request/useApi';

const BareUploadLicense: FC = () => {
  const dropzoneRef = useRef<DropzoneRef>(null);
  const [uploadHasError, setUploadHasError] = useState<boolean>(false);
  const [eulaIsOpen, setEulaIsOpen] = useState<boolean>(false);
  const { state } = useIpfLocation();
  const msg = useLocale();
  const api = useApi();
  const navigate = useNavigate();
  const ctxProps = useGlobalStateContextSelectors({
    dispatch: prop('dispatch'),
  });

  const handleDrop = async (files: File[]) => {
    try {
      const formData = new FormData();
      // Fixed noUncheckedIndexedAccess NIM-19408
      formData.append('license', files[0]!);
      const license = await api.request<
        Omit<LicenseInfoApiResponse, 'expired'>
      >({
        url: '/licenses/upload',
        data: formData,
        formData: true,
        method: 'POST',
      });
      ctxProps.dispatch(saveLicense, {
        license: { ...license, state: 'VALID' },
      });
      await sleep(0);
      setUploadHasError(false);
      navigate(state?.nextPathName !== undefined ? state.nextPathName : '/');
    } catch (e) {
      setUploadHasError(true);
      throw e;
    }
  };

  const handleSelectButton = () => {
    dropzoneRef.current?.open();
  };

  return (
    <div className='m-4'>
      <Helmet title={msg.uploadLicense.title} />
      <LTEBox
        bodyComponent={
          <div>
            <Dropzone ref={dropzoneRef} onDrop={handleDrop} multiple={false}>
              {({ getRootProps, getInputProps, isDragActive }) => (
                <>
                  <DropzoneWrapper
                    {...getRootProps()}
                    hasError={uploadHasError}
                    isOver={isDragActive}
                    fullWidth
                  >
                    <>
                      <input {...getInputProps()} />
                      {msg.uploadLicense.dropFile}
                    </>
                  </DropzoneWrapper>
                  <div className='mt-4'>
                    {msg.uploadLicense.acceptance}{' '}
                    <ButtonAsA onClick={() => setEulaIsOpen(true)}>
                      {msg.uploadLicense.eula}
                    </ButtonAsA>
                    .
                  </div>
                  {eulaIsOpen && (
                    <EulaModal onClose={() => setEulaIsOpen(false)} />
                  )}
                  <Button
                    variant='secondary'
                    className='mt-4'
                    type='button'
                    onClick={handleSelectButton}
                  >
                    {msg.uploadLicense.selectButton}
                  </Button>
                </>
              )}
            </Dropzone>
          </div>
        }
        className='box box-primary upload-license-box--maxwidth'
        title={msg.uploadLicense.title}
      />
    </div>
  );
};

export const UploadLicense = memo(BareUploadLicense);
